/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2022 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/

import * as React from "react";
import styled, { css } from "styled-components";
import { ToastMessages } from "components/shared/ToastMessages";
import Popover from "components/shared/Popover";
import { Logo, LogoMode } from "components/Logo";
import ProfileDropdown from "components/ProfileDropdown";
import {
  QuickTools,
  QuickToolsMode,
  QuickToolType,
} from "components/shared/QuickTools";
import { useRouteMatch } from "react-router-dom";
import { LocaleLink } from "utilities/NavigationUtilities";
import { devices } from "components/shared/Styles";
import { AppPath } from "components/AppPath";
import AppHeaderMenu from "./AppHeaderMenu.svg";
import { webComponentShim } from "components/shared/WebComponentShim";
import { UpgradeButton } from "components/shared/UpgradeButton";
import { useCurrentUser } from "actions/CurrentUserActions";
import { ExpressCheckoutButtonPosition } from "constants/Analytics";
import { GlobalAlertBanner } from "components/shared/AppHeader/GlobalAlertBanner";
import { AppHeaderHeight } from "components/shared/AppHeader/AppHeaderHeight";
import { useProfile } from "hooks/useProfile";
import { CurrentUserLoadingState } from "reducers/CurrentUserReducer";
export { AppHeaderHeight } from "components/shared/AppHeader/AppHeaderHeight";
import { PromotionBanner } from "components/shared/PromotionBanner";
import { WhatsNewButton } from "components/shared/AppHeader/WhatsNewButton";
import { useFloodgateFeature } from "hooks/useFloodgateFeature";
import { MyProjectsButton } from "components/shared/AppHeader/MyProjectsButton";
import { CommunityLinks } from "components/shared/AppHeader/CommunityLinks";

export const FixedAppHeader = styled.header`
  position: fixed;
  top: 0;
  box-sizing: border-box;
  height: ${AppHeaderHeight};
  display: flex;
  flex-direction: row;
  width: 100%;
  align-items: center;
  justify-content: space-between;
  z-index: 5;
  padding: 0 24px;
  background: var(--spectrum-global-color-static-white);
  border-bottom: 2px solid #f4f4f4;

  ${devices.small} {
    padding: 25px;
  }

  .show-only-on-mobile {
    ${devices.large} {
      display: none;
    }
    ${devices.medium} {
      display: flex;
    }
  }

  .hide-on-mobile {
    ${devices.small} {
      display: none;
    }
    ${devices.medium} {
      display: none;
    }
  }
`;

const LeftButtonGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;

  ${devices.small} {
    gap: 14px;
  }
`;

const RightButtonGroup = styled.div<{ loaded: boolean }>`
  display: flex;
  align-items: center;
  gap: 16px;

  transition: opacity 300ms ease-in-out;
  opacity: ${({ loaded }): number => (loaded ? 1 : 0)};
`;

const HeaderActionStyles = css`
  text-decoration: none;
  color: var(--spectrum-gray-800);
  font-size: 16px;
  -webkit-font-smoothing: antialiased;
`;

const HeaderAction = styled.span`
  ${HeaderActionStyles}
`;

const HeaderLink = styled(LocaleLink)`
  ${HeaderActionStyles}
`;

export const PaddedPopover = styled(Popover)`
  padding: 48px;
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  min-width: 326px;
  max-height: fit-content;
  align-items: start;
  border-radius: 32px;
  box-shadow: var(--phonos-box-shadow);

  ${devices.small} {
    min-width: calc(100vw - 14px);
    padding: 32px;
  }
`;

const MobileMenuContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const PlansButton: React.FC = () => {
  return (
    <HeaderLink to={AppPath.PLANS}>
      <sp-button
        dir="rtl"
        size="xl"
        variant="secondary"
        style={{ marginTop: 16 }}
      >
        {I18n.t("links.plans")}
      </sp-button>
    </HeaderLink>
  );
};

const MobileMenuButton = styled(webComponentShim("sp-action-button")).attrs({
  size: "m",
  slot: "trigger",
  quiet: true,
  dir: "rtl",
})`
  padding: 4px 0 0 0;
  width: 24px;
  height: 24px;
`;

const QuickToolsDropdown: React.FC = () => {
  return (
    <overlay-trigger placement="bottom-start" data-testid="tools-header-button">
      <div slot="trigger">
        <MobileMenuButton className="show-only-on-mobile">
          <img src={AppHeaderMenu} alt="Menu" />
        </MobileMenuButton>
        <sp-action-button
          class="hide-on-mobile"
          size="m"
          quiet={true}
          dir="rtl"
        >
          <sp-icon-chevron-down slot="icon" />
          <HeaderAction>{I18n.t("links.tools")}</HeaderAction>
        </sp-action-button>
      </div>
      <PaddedPopover>
        <QuickTools
          types={[
            QuickToolType.EnhanceSpeech,
            QuickToolType.Studio,
            QuickToolType.MicCheck,
          ]}
          mode={QuickToolsMode.Dark}
        />
        <MobileMenuContainer className="show-only-on-mobile">
          <PlansButton />
          <CommunityLinks mobile={true} />
          <WhatsNewButton mobile={true} />
        </MobileMenuContainer>
      </PaddedPopover>
    </overlay-trigger>
  );
};

export const AppHeader: React.FC<{ mode?: LogoMode }> = ({
  mode = LogoMode.DEFAULT,
}) => {
  const { currentUser, currentUserLoadingState } = useCurrentUser();
  const { isLoading: isLoadingProfile } = useProfile(currentUser?.adobeId);
  const hasLoaded =
    (currentUserLoadingState === CurrentUserLoadingState.LOADED &&
      !isLoadingProfile) ||
    currentUserLoadingState === CurrentUserLoadingState.FAILED;
  const isStudioMode = mode === LogoMode.STUDIO;
  const { path } = useRouteMatch();
  const isStudioProject = path.includes("/projects");
  const inStudio = isStudioProject || path.includes(AppPath.STUDIO);
  // We can use this feature flag to toggle the promotion banner across the app
  // However the text for the banner is in the internationalization file to support multiple languages
  // To change the text we need a PR but to toggle the banner we just use the feature flag
  // making the banner a bit more reusable.
  //
  // if requirements change we can also revisit this approach
  // https://git.corp.adobe.com/Voice/phonos-server/pull/4028
  // where we can pass the text of the banner from the feature flag
  // however this would not support internationalization.
  const isPromotionBannerVisible = useFloodgateFeature("promotion_banner");

  return (
    <>
      <FixedAppHeader>
        <LeftButtonGroup>
          <div className="show-only-on-mobile">
            {!isStudioMode && <QuickToolsDropdown />}
          </div>
          <Logo mode={mode} />
          <div className="hide-on-mobile">
            {!isStudioMode && <QuickToolsDropdown />}
          </div>
          <div className="hide-on-mobile">
            {!isStudioMode && (
              <HeaderLink to={AppPath.PLANS} data-testid="features-header-link">
                {I18n.t("links.plans")}
              </HeaderLink>
            )}
          </div>
          <div className="hide-on-mobile">
            {!isStudioMode && <CommunityLinks />}
          </div>
          <div className="hide-on-mobile">
            {!isStudioMode && <WhatsNewButton />}
          </div>
        </LeftButtonGroup>
        <RightButtonGroup loaded={hasLoaded}>
          {!inStudio && currentUser && (
            <div className="hide-on-mobile">
              <MyProjectsButton />
            </div>
          )}
          {hasLoaded && (
            <>
              <div className="hide-on-mobile">
                <UpgradeButton
                  position={
                    ExpressCheckoutButtonPosition.UPGRADE_BUTTON_POPUP_HEADER
                  }
                />
              </div>
              <ProfileDropdown />
            </>
          )}
        </RightButtonGroup>
        <ToastMessages />
      </FixedAppHeader>
      <GlobalAlertBanner />
      {!isStudioProject && (
        <PromotionBanner isVisible={isPromotionBannerVisible} />
      )}
    </>
  );
};
